import { Smartphone, X } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export default function PwaBanners() {
  const {
    isStandalone,
    isBannerDismissed,
    pwaInstalled,
    isIOS,
    isSafari,
    deferredPrompt,
    showIOSGuide,
    setShowIOSGuide,
    handleDismissBanner,
    handleInstallClick,
  } = useAppContext();

  return (
    <>
      {/* PWA Open in App Banner */}
      {!isStandalone && !isBannerDismissed && pwaInstalled && (
        <div className="no-print bg-stone-900 border-b border-stone-800 text-stone-100 px-4 py-3 flex items-center justify-between shadow-md relative z-50 animate-in slide-in-from-top duration-350">
          <div className="flex items-center space-x-3 max-w-7xl mx-auto w-full">
            <div className="p-2 bg-stone-800 rounded-lg hidden sm:block">
              <Smartphone className="w-5 h-5 text-brand-400" />
            </div>
            <div className="flex-1 text-xs">
              <p className="font-semibold text-stone-200">
                Uygulama Zaten Yüklü!
              </p>
              <p className="text-[10px] text-stone-400 mt-0.5">
                Portal ana ekranınızda bir uygulama olarak yüklü durumda. Daha akıcı bir deneyim için tarayıcı yerine uygulamayı kullanabilirsiniz.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-xs text-stone-400 hidden sm:block bg-stone-850 px-2.5 py-1.5 rounded border border-stone-800">
                {isIOS 
                  ? "👉 Ana ekranınızdaki 'İntema Teklif' simgesine dokunarak açın."
                  : "👉 Tarayıcı adres çubuğundaki \"Uygulamada Aç\" simgesine tıklayın."
                }
              </div>
              <button
                onClick={handleDismissBanner}
                className="text-stone-400 hover:text-stone-200 p-1 hover:bg-stone-800 rounded transition-colors"
                aria-label="Kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Banner */}
      {!isStandalone && !isBannerDismissed && !pwaInstalled && (deferredPrompt || (isIOS && isSafari)) && (
        <div className="no-print bg-brand-600 text-white px-4 py-3 flex items-center justify-between shadow-md relative z-50 border-b border-brand-700 animate-in slide-in-from-top duration-350">
          <div className="flex items-center space-x-3 max-w-7xl mx-auto w-full">
            <div className="p-2 bg-brand-700/50 rounded-lg hidden sm:block">
              <Smartphone className="w-5 h-5 text-brand-100" />
            </div>
            <div className="flex-1 text-sm">
              <p className="font-semibold tracking-wide">
                İntema Mutfak Teklif Portalı'nı Uygulama Olarak Kullanın!
              </p>
              <p className="text-xs text-brand-100 mt-0.5">
                {isIOS && isSafari 
                  ? "Çevrimdışı çalışabilmek ve hızlı erişmek için paylaş butonuna tıklayıp 'Ana Ekrana Ekle' seçeneğini seçebilirsiniz."
                  : "Uygulamayı yükleyerek masaüstünüzden veya telefonunuzdan tek tıkla, daha hızlı ve çevrimdışı kullanabilirsiniz."
                }
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {deferredPrompt && (
                <button
                  onClick={handleInstallClick}
                  className="bg-white hover:bg-brand-50 text-brand-700 px-4 py-1.5 rounded-md text-xs font-semibold shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  Şimdi Yükle
                </button>
              )}
              {isIOS && isSafari && (
                <button
                  onClick={() => setShowIOSGuide(true)}
                  className="bg-white hover:bg-brand-50 text-brand-700 px-4 py-1.5 rounded-md text-xs font-semibold shadow-sm transition-all hover:scale-[1.02]"
                >
                  Nasıl Yüklenir?
                </button>
              )}
              <button
                onClick={handleDismissBanner}
                className="text-white/80 hover:text-white p-1 hover:bg-white/10 rounded transition-colors"
                aria-label="Kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iOS Safari Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white text-stone-900 rounded-xl max-w-sm w-full p-6 shadow-2xl relative border border-stone-100 animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowIOSGuide(false)}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 p-1 rounded-full hover:bg-stone-100 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-brand-50 rounded-full flex items-center justify-center mb-4">
                <Smartphone className="w-6 h-6 text-brand-600" />
              </div>
              <h3 className="font-bold text-lg text-stone-900 mb-2">iOS Safari Kurulum Kılavuzu</h3>
              <p className="text-xs text-stone-500 mb-6">
                İntema Mutfak uygulamasını iPhone veya iPad'inize eklemek için aşağıdaki adımları takip edin:
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-stone-100 rounded-full flex items-center justify-center font-bold text-xs text-stone-650 shrink-0 mt-0.5">
                  1
                </div>
                <div className="text-sm">
                  <p className="font-medium text-stone-850">Paylaş Butonuna Tıklayın</p>
                  <p className="text-xs text-stone-500 mt-0.5">Safari tarayıcısının alt kısmındaki paylaş (ok yukarı) simgesine dokunun.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-stone-100 rounded-full flex items-center justify-center font-bold text-xs text-stone-650 shrink-0 mt-0.5">
                  2
                </div>
                <div className="text-sm">
                  <p className="font-medium text-stone-850">"Ana Ekrana Ekle"yi Seçin</p>
                  <p className="text-xs text-stone-500 mt-0.5">Açılan paylaşım menüsünde aşağı kaydırıp "Ana Ekrana Ekle" seçeneğini bulun ve seçin.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-stone-100 rounded-full flex items-center justify-center font-bold text-xs text-stone-650 shrink-0 mt-0.5">
                  3
                </div>
                <div className="text-sm">
                  <p className="font-medium text-stone-850">"Ekle" Butonuna Basın</p>
                  <p className="text-xs text-stone-500 mt-0.5">Sağ üst köşedeki "Ekle" butonuna basarak işlemi tamamlayın. Uygulama artık ana ekranınızda!</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowIOSGuide(false)}
              className="mt-6 w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-2.5 rounded-lg text-xs transition-colors shadow-sm"
            >
              Anladım, Kapat
            </button>
          </div>
        </div>
      )}
    </>
  );
}
